/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import * as yup from "yup";
import { useFormik } from "formik";
import { signIn, getCurrentUser, signOut } from "aws-amplify/auth";
import { useAppDispatch } from "@store/store";
import { EMAIL } from "@utils/expreciones";
import { jwtDecode } from "jwt-decode";
import CustomRightImage from "@components/CustomRightImagev2";
import {
  setCurretStepSecondGuarantee,
  setStatusConfigSecondGuarantee,
  setTaxSystemSecondGuarantee,
  setUserSecondGuaranteeAddressInfo,
  setUserSecondGuaranteeInfo,
  setUserSecondGuaranteePersonalInfo,
  setUserSecondGuaranteeUser,
} from "@store/slices/segundoAvalSlice";
import { useLazyGetSessionInfoGuaranteeQuery } from "@api/second_guarantee_api/sessionInfoGuarantee";
import dayjs from "dayjs";
import { useLazyGetStatusSecondGuaranteeQuery } from "@api/second_guarantee_api/catalogsSecondGuarantee";
import {
  PODUCT_ID_SECOND_GUARANTEE,
  SECOND_GUARANTEE_STATUS_INITIAL,
  TAX_SYSTEM_TYPE_SECONDGUARANTEE,
  TAX_SYTEM_ID_SECOND_GUARANTE,
} from "@helpers/constantes";
import { useLazyGetLoanApplicationByIdSecondGuaranteeQuery, usePatchUsuarioStatusSecondGuaranteeMutation } from "@api/second_guarantee_api/secondGuaranteeApi";
import { setLoanApplication } from "@store/slices/registerSlice";

interface TokenPayload {
  email: string;
  name: string;
  exp: number;
  password: string;
}

export const LoginSegundoAval = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [error, setError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [triggerGetSessionInfo] = useLazyGetSessionInfoGuaranteeQuery();
  const [triggerGetStatusLits] = useLazyGetStatusSecondGuaranteeQuery();
  const [patchStatus] = usePatchUsuarioStatusSecondGuaranteeMutation();
  const [getLoadApplication] = useLazyGetLoanApplicationByIdSecondGuaranteeQuery();

  // Función para limpiar sesiones de manera segura
  const clearPreviousSession = async () => {
    try {
      await signOut({ global: false });
      console.log("Sesión anterior limpiada");
    } catch (error: any) {
      console.log("No había sesión anterior:", error?.message);
    }
  };

  const initialValues = {
    email: "",
    password: "",
  };

  const formik = useFormik({
    initialValues,
    validationSchema: yup.object().shape({
      email: yup
        .string()
        .required("Campo requerido")
        .matches(EMAIL, "Asegúrate de incluir el @ y los puntos necesarios (.com .mx)"),
      password: yup.string().required("Campo requerido"),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        setError(false);

        // Limpiar sesión previa usando la función helper
        await clearPreviousSession();

        // Pequeña pausa para asegurar que el signOut se complete
        await new Promise(resolve => setTimeout(resolve, 500));

        const signedResult = await signIn({
          username: values.email,
          password: values.password,
          options: {
            authFlowType: "USER_PASSWORD_AUTH",
          },
        });

        if (signedResult.isSignedIn && signedResult.nextStep.signInStep === "DONE") {
          const currentUser = await getCurrentUser();

          if (!currentUser?.userId) {
            console.warn("No se encontró userId en currentUser");
            return;
          }

          const sessionInfo = await triggerGetSessionInfo({ id: currentUser.userId }).unwrap();

          const statusResponse = await triggerGetStatusLits({
            product_id: PODUCT_ID_SECOND_GUARANTEE,
            tax_system_id: TAX_SYTEM_ID_SECOND_GUARANTE,
          }).unwrap();

          const responseApplication = await getLoadApplication(sessionInfo.loan_application_data.id).unwrap();
          
          dispatch(setStatusConfigSecondGuarantee(statusResponse));

          dispatch(
            setUserSecondGuaranteeInfo({
              id: sessionInfo.user_endorsement_data.person_id,
              loan_application_id: sessionInfo.loan_application_data.id,
              client_details_id: sessionInfo.loan_application_data.client_details_id,
              user_id: sessionInfo.user_endorsement_data.id,
            }),
          );

          dispatch(
            setUserSecondGuaranteePersonalInfo({
              name: sessionInfo.person.name,
              first_lastname: sessionInfo.person.last_name,
              second_lastname: sessionInfo.person.last_name_2,
              date_birth: sessionInfo.person.birth_date
                ? dayjs(sessionInfo.person.birth_date).format("YYYY-MM-DD")
                : "",
              birth_state: String(sessionInfo.person.birth_state),
              code_phone_number: sessionInfo.person.code_phone_number,
              phone_number: sessionInfo.person.phone_number,
              curp: sessionInfo.person.curp,
              rfc: sessionInfo.person.rfc ? sessionInfo.person.rfc.slice(0, 10) : "",
              rfc_homoclave: sessionInfo.person.rfc ? sessionInfo.person.rfc.slice(10, 13) : "",
              sex: sessionInfo.person.sex,
              email: sessionInfo.person.email,
            }),
          );

          dispatch(setLoanApplication(responseApplication));

          const address = sessionInfo?.address ?? {};

          dispatch(
            setUserSecondGuaranteeAddressInfo({
              id: address.id || "",
              street: address.street || "",
              no_ext: address.no_ext || "",
              no_int: address.no_int || "",
              colony_id: address.colonia_id || 0,
              state_id: address.state_id || 0,
              city_id: address.city_id || 0,
              locality_id: address.locality_id || 0,
              postal_code: address.cp || "",
              state_name: address.state || "",
              city_name: address.municipality || "",
              locality_name: address.locality || "",
              other_neighborhood: address.other_neighborhood || "",
            }),
          );

          if (
            !sessionInfo.current_screen ||
            sessionInfo.current_screen.url === null ||
            sessionInfo.current_screen.url === ""
          ) {
            await patchStatus({
              usuario: sessionInfo.user_endorsement_data.id,
              body: {
                status_id: SECOND_GUARANTEE_STATUS_INITIAL.status_id,
              },
            }).unwrap();

            dispatch(
              setCurretStepSecondGuarantee({
                code: SECOND_GUARANTEE_STATUS_INITIAL.code,
                url: SECOND_GUARANTEE_STATUS_INITIAL.url,
                sequence: SECOND_GUARANTEE_STATUS_INITIAL.sequence,
                status_id: SECOND_GUARANTEE_STATUS_INITIAL.status_id,
              }),
            );

            dispatch(setTaxSystemSecondGuarantee({ tax_system_type: TAX_SYSTEM_TYPE_SECONDGUARANTEE.PERSON.code }));

            navigate(SECOND_GUARANTEE_STATUS_INITIAL.url);
          } else {
            dispatch(
              setCurretStepSecondGuarantee({
                code: String(sessionInfo.current_screen.code),
                url: String(sessionInfo.current_screen.url),
                sequence: Number(sessionInfo.current_screen.sequence),
                status_id: sessionInfo.user_endorsement_data.status_id,
              }),
            );
            dispatch(setTaxSystemSecondGuarantee({ tax_system_type: TAX_SYSTEM_TYPE_SECONDGUARANTEE.PERSON.code }));
            navigate(String(sessionInfo.current_screen.url));
          }
        } else if (signedResult.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
          navigate("/avales/cambiar_contrasena_aval");
        }
      } catch (error: any) {
        console.error("Error completo en login:", error);
        console.error("Código de error:", error?.code);
        console.error("Mensaje de error:", error?.message);
        console.error("Nombre del error:", error?.name);
        setError(true);
      } finally {
        setLoading(false);
      }
    },
  });

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleForgotPasswordClick = () => {
    navigate("/avales/cambiar_contrasena_aval_adicional");
  };

  useEffect(() => {
    const getQueryParams = async () => {
      try {
        // Limpiar sesión al cargar la página
        await clearPreviousSession();

        const searchParams = new URLSearchParams(location.search);

        const token = searchParams.get("token");
        if (!token) {
          navigate("/avales/liga_caducada");
          return;
        }

        const decoded = jwtDecode<TokenPayload>(String(token));

        const now = Math.floor(Date.now() / 1000);

        if (decoded.exp < now) {
          navigate("/avales/liga_caducada");
        }

        formik.setFieldValue("email", decoded.email);

        dispatch(setUserSecondGuaranteeUser({ email: decoded.email }));
      } catch (error) {
        console.log("Ha ocurrido un error", error);
      }
    };

    getQueryParams();
  }, []);

  const onChangeEmail = (value: string) => {
    const low = value.toLowerCase().trim();
    formik.setFieldValue("email", low);
  };

  return (
    <Grid container width="100%" height="100%">
      <Grid item xs={12} md={8}>
        <Box
          sx={{
            mt: isMobileOrTablet ? 4 : 8,
            mx: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography
              variant={isMobileOrTablet ? "h3" : "h2"}
              fontWeight={theme?.palette?.fontWeights?.bold}
              color={theme?.palette?.primary?.dark}
            >
              Te damos la bienvenida
            </Typography>
          </Box>

          <Box mt={isMobileOrTablet ? 2 : 3}>
            <Typography variant={isMobileOrTablet ? "body2" : "body1"} color={theme?.palette?.primary?.main}>
              Ingresa los datos para iniciar sesión
            </Typography>
          </Box>

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3 }}>
            <form onSubmit={formik.handleSubmit} noValidate>
              <FormControl fullWidth>
                <TextField
                  id="email"
                  name="email"
                  label="Correo electrónico"
                  margin="normal"
                  required
                  fullWidth
                  autoComplete="off"
                  onChange={(e) => onChangeEmail(e.target.value)}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  value={formik.values.email}
                />
              </FormControl>
              <FormControl fullWidth>
                <TextField
                  id="password"
                  name="password"
                  label="Contraseña"
                  type={showPassword ? "text" : "password"}
                  margin="normal"
                  required
                  fullWidth
                  autoComplete="off"
                  onChange={(e) => formik.setFieldValue("password", e.target.value.trim())}
                  onBlur={formik.handleBlur}
                  value={formik.values.password}
                  sx={{ my: 2 }}
                  error={formik.touched.password && Boolean(formik.errors.password)}
                  helperText={formik.touched.password && formik.errors.password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          edge="end"
                          sx={{ color: theme?.palette?.primary?.main }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </FormControl>

              {error && (
                <Box sx={{ mt: 2 }}>
                  <Alert variant="outlined" severity="error">
                    Credenciales incorrectas. Verifica tu correo electrónico y contraseña.
                  </Alert>
                </Box>
              )}

              <Box mt={isMobileOrTablet ? 2 : 4} display="flex" justifyContent="center">
                <Link
                  onClick={handleForgotPasswordClick}
                  sx={{ display: "inline-block", width: "fit-content", cursor: "pointer" }}
                >
                  <Typography textAlign="center" variant="body2">
                    Olvidé mi contraseña
                  </Typography>
                </Link>
              </Box>

              <Box textAlign={"center"} mt={isMobileOrTablet ? 2 : 4}>
                <LoadingButton variant="default" type="submit" disabled={!formik.isValid || !formik.dirty || loading}>
                  Continuar
                </LoadingButton>
              </Box>
            </form>
          </Container>
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4} textAlign={"center"}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};
